import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { calculateRentalYield } from './calculation'
import { content, faqs } from './content'
import type { RentalYieldInputs, RentalYieldResult } from './types'

const schema = z.object({
  purchasePrice: positiveMoney('Enter a purchase price').refine((v) => v > 0, {
    message: 'Purchase price must be greater than 0',
  }),
  deposit: positiveMoney(),
  bondInterestRate: z.number().min(0).max(1),
  bondTermYears: z.number().int().min(1).max(40),
  monthlyRent: positiveMoney('Enter a monthly rent').refine((v) => v >= 0, {
    message: 'Rent must be at least 0',
  }),
  vacancyRate: z.number().min(0).max(1),
  leviesMonthly: positiveMoney(),
  ratesMonthly: positiveMoney(),
  maintenancePercent: z.number().min(0).max(1),
  managementPercent: z.number().min(0).max(1),
})

const defaults: RentalYieldInputs = {
  purchasePrice: 1_200_000,
  deposit: 240_000,
  bondInterestRate: 0.115,
  bondTermYears: 20,
  monthlyRent: 10_000,
  vacancyRate: 0.05,
  leviesMonthly: 1_800,
  ratesMonthly: 1_200,
  maintenancePercent: 0.05,
  managementPercent: 0.08,
}

export const rentalYieldDefinition: CalculatorDefinition<RentalYieldInputs, RentalYieldResult> = {
  id: 'rental-yield',
  slug: 'rental-yield-calculator',
  category: 'home',
  title: 'Rental Yield & Property ROI Calculator',
  shortTitle: 'Rental Yield',
  description:
    'Calculate gross yield, net yield, annual cash flow and cash-on-cash return for South African rental property.',
  inputs: [
    {
      name: 'purchasePrice',
      label: 'Purchase price',
      type: 'money',
      required: true,
      unit: 'R',
      placeholder: 'e.g. 1 200 000',
    },
    {
      name: 'deposit',
      label: 'Deposit',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'bondInterestRate',
      label: 'Bond interest rate',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.001,
      defaultValue: 0.115,
    },
    {
      name: 'bondTermYears',
      label: 'Bond term',
      type: 'number',
      required: true,
      unit: 'years',
      min: 1,
      max: 40,
      step: 1,
      defaultValue: 20,
    },
    {
      name: 'monthlyRent',
      label: 'Monthly rent',
      type: 'money',
      required: true,
      unit: 'R',
      placeholder: 'e.g. 10 000',
    },
    {
      name: 'vacancyRate',
      label: 'Vacancy rate',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.05,
    },
    {
      name: 'leviesMonthly',
      label: 'Levies (monthly)',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'ratesMonthly',
      label: 'Rates & taxes (monthly)',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'maintenancePercent',
      label: 'Maintenance (of gross rent)',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.05,
    },
    {
      name: 'managementPercent',
      label: 'Management fees (of gross rent)',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.08,
    },
  ],
  defaults,
  schema,
  calculate: calculateRentalYield,
  resultSections: [
    {
      id: 'grossYield',
      label: 'Gross rental yield',
      format: 'percent',
      value: (r: RentalYieldResult) => r.grossYield,
      highlighted: true,
    },
    {
      id: 'netYield',
      label: 'Net rental yield',
      format: 'percent',
      value: (r: RentalYieldResult) => r.netYield,
      highlighted: true,
    },
    {
      id: 'annualCashFlow',
      label: 'Annual cash flow',
      format: 'currency',
      value: (r: RentalYieldResult) => r.annualCashFlow,
      highlighted: true,
    },
    {
      id: 'cashOnCashReturn',
      label: 'Cash-on-cash return',
      format: 'percent',
      value: (r: RentalYieldResult) => r.cashOnCashReturn,
    },
    {
      id: 'monthlyBondPayment',
      label: 'Monthly bond payment',
      format: 'currency',
      value: (r: RentalYieldResult) => r.monthlyBondPayment,
    },
    {
      id: 'bondAmount',
      label: 'Bond amount',
      format: 'currency',
      value: (r: RentalYieldResult) => r.bondAmount,
    },
    {
      id: 'operatingExpenses',
      label: 'Annual operating expenses',
      format: 'currency',
      value: (r: RentalYieldResult) => r.operatingExpenses,
    },
    {
      id: 'netOperatingIncome',
      label: 'Net operating income',
      format: 'currency',
      value: (r: RentalYieldResult) => r.netOperatingIncome,
    },
  ],
  assumptions: [
    {
      name: 'Standard bond amortisation and rental property operating assumptions',
      reviewedAt: '2026-09-11',
      effectiveFrom: '2026-09-01',
      notes:
        'Gross yield = annual rent / purchase price. Net yield = net operating income / purchase price. Cash-on-cash = annual cash flow / deposit. Excludes capital appreciation, taxes and bond initiation costs.',
    },
  ],
  related: ['bond-calculator', 'transfer-cost-calculator'],
  seo: {
    title: 'Rental Yield Calculator South Africa',
    description:
      'Calculate gross vs net rental yield, annual cash flow and cash-on-cash return for South African investment property purchases.',
    keywords: [
      'rental yield calculator',
      'property ROI South Africa',
      'rental property calculator',
    ],
  },
  content,
  faqs,
}
